import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { User } from '@nousmedis/guard-types/frontend';
import {
  BehaviorSubject,
  Observable,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  map,
  startWith,
} from 'rxjs';

/**
 * Estado operativo de una aplicación en el selector.
 *
 * - `running`     — la app está activa y accesible.
 * - `offline`     — la app no responde o está caída.
 * - `maintenance` — la app está temporalmente fuera de servicio por mantenimiento.
 * - `disabled`    — la app está deshabilitada para el usuario o la organización.
 */
export enum AppStatus {
  Running     = 'running',
  Offline     = 'offline',
  Maintenance = 'maintenance',
  Disabled    = 'disabled',
}

/**
 * Representa una entrada de aplicación en el selector de apps del usuario.
 * Todos los campos marcados como opcionales mantienen compatibilidad con
 * entradas existentes que no los declaren.
 */
export interface AppEntry {
  /** Identificador único de la organización propietaria de la app. */
  orgId: string;
  /** Nombre visible de la organización. */
  orgName: string;
  /** Identificador único de la aplicación. */
  appId: string;
  /** Nombre visible de la aplicación. */
  appName: string;
  /**
   * Estado operativo de la app.
   * Usar el enum `AppStatus`. Por defecto se considera `AppStatus.Running`
   * si el valor no está presente.
   */
  status: AppStatus;
  /** URL del icono o avatar representativo de la app. Opcional. */
  icon?: string;
  /** Descripción breve de la funcionalidad de la app. Opcional. */
  description?: string;
}

/** Duración en ms antes de emitir retry automático cuando loading no resuelve. */
const LOADING_TIMEOUT_MS = 10_000;
/** Duración en ms durante la cual el botón de retry permanece deshabilitado. */
const RETRY_COOLDOWN_MS = 2_000;

@Component({
  selector: 'sw-app-selector',
  templateUrl: './app-selector.component.html',
  styleUrls: ['./app-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppSelectorComponent implements OnInit, OnChanges, OnDestroy {
  /** Referencia al enum expuesta para que el template pueda comparar valores. */
  readonly AppStatus = AppStatus;

  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  /** BehaviorSubject interno para reaccionar a cambios del @Input apps. */
  private readonly apps$ = new BehaviorSubject<AppEntry[]>([]);

  /** Handle del timeout de carga activo, si lo hay. */
  private loadingTimeoutId: ReturnType<typeof setTimeout> | null = null;

  /** Handle del timeout de cooldown de retry activo, si lo hay. */
  private retryTimeoutId: ReturnType<typeof setTimeout> | null = null;

  /** FormControl para el input de búsqueda. */
  readonly searchControl = new FormControl<string>('', { nonNullable: true });

  /** Observable con la lista de apps filtradas según el término de búsqueda. */
  filteredApps$!: Observable<AppEntry[]>;

  /** Observable con el número de apps que coinciden con el filtro actual. */
  filteredCount$!: Observable<number>;

  /**
   * True durante los 2 segundos posteriores a emitir un retry, para evitar
   * pulsaciones repetidas mientras el padre gestiona la petición.
   */
  isRetrying = false;

  @Input({ required: true }) user!: User;

  @Input({ required: true })
  set apps(value: AppEntry[]) {
    if (!Array.isArray(value)) {
      console.warn('[AppSelector]', new Date().toISOString(), 'apps input is not an array');
      this.apps$.next([]);
      return;
    }
    this.apps$.next(value);
  }

  @Input() loading = false;

  /** Mensaje de error externo. Cuando no es null se muestra el error state. */
  @Input() error: string | null = null;

  /** Array de 6 items usado en el ngFor del skeleton de carga. */
  readonly skeletonItems = Array(6);

  @Output() appSelected = new EventEmitter<AppEntry>();
  @Output() logoutRequest = new EventEmitter<void>();
  /** Emitido cuando el usuario pulsa el botón de reintentar en el error state. */
  @Output() retry = new EventEmitter<void>();

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['error']) {
      this.cdr.markForCheck();
    }

    if (changes['loading']) {
      const isNowLoading = changes['loading'].currentValue as boolean;
      if (isNowLoading) {
        this.startLoadingTimeout();
      } else {
        this.clearLoadingTimeout();
      }
    }
  }

  ngOnInit(): void {
    const search$ = this.searchControl.valueChanges.pipe(
      startWith(this.searchControl.value),
      debounceTime(300),
      distinctUntilChanged(),
    );

    this.filteredApps$ = combineLatest([this.apps$, search$]).pipe(
      map(([apps, term]) => {
        const normalized = term.trim().toLowerCase();
        if (!normalized) {
          return apps;
        }
        return apps.filter(
          (app) =>
            app.appName.toLowerCase().includes(normalized) ||
            app.orgName.toLowerCase().includes(normalized),
        );
      }),
      takeUntilDestroyed(this.destroyRef),
    );

    this.filteredCount$ = this.filteredApps$.pipe(
      map((apps) => apps.length),
    );
  }

  ngOnDestroy(): void {
    this.clearLoadingTimeout();
    this.clearRetryTimeout();
  }

  clearSearch(): void {
    this.searchControl.setValue('');
  }

  onSelectApp(app: AppEntry): void {
    this.appSelected.emit(app);
  }

  onLogout(): void {
    this.logoutRequest.emit();
  }

  onRetry(): void {
    if (this.isRetrying) {
      return;
    }
    this.emitRetry();
  }

  trackByApp(_index: number, app: AppEntry): string {
    return `${app.orgId}-${app.appId}`;
  }

  /**
   * Inicia el timeout de carga. Si `loading` sigue siendo `true` tras
   * `LOADING_TIMEOUT_MS`, emite retry automáticamente y registra el error
   * interno en el input `error`.
   */
  private startLoadingTimeout(): void {
    this.clearLoadingTimeout();
    this.loadingTimeoutId = setTimeout(() => {
      if (this.loading) {
        this.error = 'USER.LOADING_TIMEOUT_ERROR';
        this.emitRetry();
      }
    }, LOADING_TIMEOUT_MS);
  }

  private clearLoadingTimeout(): void {
    if (this.loadingTimeoutId !== null) {
      clearTimeout(this.loadingTimeoutId);
      this.loadingTimeoutId = null;
    }
  }

  /**
   * Emite el output `retry`, activa `isRetrying` y lo desactiva tras
   * `RETRY_COOLDOWN_MS`. Llama a `markForCheck` para reflejar el estado
   * con OnPush.
   */
  private emitRetry(): void {
    this.retry.emit();
    this.isRetrying = true;
    this.cdr.markForCheck();

    this.clearRetryTimeout();
    this.retryTimeoutId = setTimeout(() => {
      this.isRetrying = false;
      this.retryTimeoutId = null;
      this.cdr.markForCheck();
    }, RETRY_COOLDOWN_MS);
  }

  private clearRetryTimeout(): void {
    if (this.retryTimeoutId !== null) {
      clearTimeout(this.retryTimeoutId);
      this.retryTimeoutId = null;
    }
  }
}
